import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LuxuryProductImage from "./LuxuryProductImage";
import { MarketplaceBadge } from "./MarketplaceLogo";

/**
 * TrendingCarousel — Master prompt v2.1 / Rule 6.
 *
 * Fetches the top trending high-ticket items (>= $150 base cost) over a
 * rolling 7-day window from the DHC HQ analytics view
 * `weekly_trending_high_ticket` and renders them as a horizontally
 * scrolling row above the catalog grid.
 *
 *   - Identifier-anchored: relies on product_sku from the trending API,
 *     never resolves images by title.
 *   - Layout-preserving: uses the same LuxuryProductImage + onError
 *     fallback chain as the main grid, so the page does not need any
 *     viewport changes (Rule 6: "Do not alter the functional layout
 *     structure of the UI components").
 *   - Server-only metrics: only the public landed price is rendered.
 *     base_cost, wholesale and 20% margin numbers never reach the
 *     browser - the API strips them server-side.
 *
 * Usage:
 *   import TrendingCarousel from "./components/TrendingCarousel";
 *   <TrendingCarousel api={API} destination={destination} currency={currency} />
 */
export default function TrendingCarousel({
  api,
  destination = "BB",
  currency = "USD",
  limit = 12,
  title = "Trending across the Mansion",
  testId = "trending-carousel",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const url = `${api}/products/trending-high-ticket?destination=${encodeURIComponent(
      destination
    )}&currency=${encodeURIComponent(currency)}&limit=${limit}`;
    fetch(url, { headers: { "Cache-Control": "no-cache" } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        const products = Array.isArray(data?.products) ? data.products : [];
        setItems(products);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, destination, currency, limit]);

  if (!loading && !items.length && !error) return null;

  return (
    <section
      className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 mb-2"
      data-testid={testId}
    >
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900">
          {title}
        </h2>
        <span className="text-[11px] uppercase tracking-wider text-emerald-700">
          Rolling 7-day · high-ticket
        </span>
      </div>
      <div
        className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-none snap-x snap-mandatory"
        style={{ scrollbarWidth: "none" }}
      >
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="shrink-0 snap-start w-44 sm:w-52 h-56 sm:h-64 bg-slate-100 rounded-lg animate-pulse"
              />
            ))
          : items.map((product) => (
              <Link
                key={product.product_id}
                to={`/products/${product.product_id}`}
                className="shrink-0 snap-start w-44 sm:w-52 rounded-lg overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition-shadow"
              >
                <div className="aspect-square bg-slate-50 relative">
                  <LuxuryProductImage
                    product={product}
                    alt={product.name || product.title}
                    testId={`trending-image-${product.product_id}`}
                  />
                  <div className="absolute top-2 left-2 z-10 pointer-events-none">
                    <MarketplaceBadge
                      marketplace={product.marketplace}
                      size="xs"
                      showLabel={false}
                    />
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-xs font-semibold text-slate-900 line-clamp-2 min-h-[32px]">
                    {product.name || product.title}
                  </h3>
                  <p className="mt-1 text-[10px] text-slate-500">
                    {product.category || "General"}
                  </p>
                  <p className="mt-2 text-sm font-bold text-emerald-700">
                    {Number(product.landed_price || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: currency || "USD",
                    })}
                  </p>
                </div>
              </Link>
            ))}
      </div>
    </section>
  );
}
